import type { ErrorEvent } from "@sentry/tanstackstart-react";
import * as Sentry from "@sentry/tanstackstart-react";
import { redactString, redactValue } from "@crm/errors";

/**
 * 是否已初始化 Sentry
 */
let isInitialized = false;

/**
 * Sentry 初始化配置
 *
 * 用于集成 Sentry 错误监控和性能追踪到 TanStack Start + Cloudflare Workers 环境
 *
 * 功能:
 * - 自动错误捕获和上报
 * - 性能追踪
 * - 用户面包屑记录
 * - 敏感信息过滤
 *
 * @example
 * ```ts
 * // 在 start.ts 中调用
 * initSentry();
 * ```
 */
export function initSentry(): void {
  // 避免重复初始化
  if (isInitialized) {
    return;
  }

  // 在本地开发环境中跳过 Sentry 初始化
  // import.meta.env 只在 Vite 本地开发环境中可用
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
    isInitialized = true; // 标记已初始化，避免重复打印
    return;
  }

  // 获取环境变量
  // 使用 VITE_SENTRY_DSN (Vite 会在构建时注入)
  // 注意: Sentry DSN 是设计为公开的，客户端需要它来发送错误
  let dsn: string | undefined;
  let environment: "production" | "preview" | "development" = "production";

  if (typeof import.meta !== "undefined" && import.meta.env) {
    dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
    const mode = import.meta.env.MODE;
    environment = mode === "production" ? "production" : "preview";
  }

  if (!dsn) {
    return;
  }

  // 初始化 Sentry
  Sentry.init({
    dsn,
    environment,

    // ✅ 启用结构化日志 (高级版功能)
    enableLogs: true,

    // 性能追踪采样率
    // ✅ 高级版已购买,恢复到推荐值
    // 生产环境: 10% 请求启用追踪
    // 非生产环境: 100% 请求启用追踪
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,

    // 错误采样率
    // ✅ 高级版已购买,恢复到 100%
    sampleRate: 1.0,

    // 集成配置
    // 注意: @sentry/tanstackstart-react 目前不支持 replayIntegration
    // 如需 Session Replay，需要在客户端单独配置或等待官方支持
    integrations: [],

    // Session Replay 采样率 (需要 replayIntegration 才生效)
    // 暂时禁用，等待 @sentry/tanstackstart-react 支持
    // replaysSessionSampleRate: environment === "production" ? 0.1 : 1.0,
    // replaysOnErrorSampleRate: 1.0,

    // 面包屑配置 (用户操作路径记录)
    maxBreadcrumbs: 50,

    // 过滤敏感信息和不重要的错误
    beforeSend(event: ErrorEvent): ErrorEvent | null {
      // 1. 过滤常见的浏览器扩展错误 (节省配额)
      const message = event.message || event.exception?.values?.[0]?.value || "";

      // 过滤浏览器扩展错误
      if (
        message.includes("chrome-extension://") ||
        message.includes("moz-extension://") ||
        message.includes("safari-extension://")
      ) {
        return null; // 不上报
      }

      // 过滤网络错误 (通常是用户网络问题,不是代码 bug)
      if (
        message.includes("NetworkError") ||
        message.includes("Failed to fetch") ||
        message.includes("Load failed")
      ) {
        return null; // 不上报
      }

      // 过滤 React hydration 错误 (通常不重要)
      if (message.includes("Minified React error")) {
        return null; // 不上报
      }

      // Apply one recursive policy to request data, headers, breadcrumbs, and extras.
      // Do not mutate the original exception/cause graph: it may be used by the app.
      const request = event.request
        ? {
            ...event.request,
            ...(event.request.data !== undefined ? { data: redactValue(event.request.data) } : {}),
            ...(event.request.headers !== undefined
              ? { headers: redactValue(event.request.headers) as Record<string, string> }
              : {}),
          }
        : undefined;
      const safeEvent = {
        ...event,
        ...(request ? { request } : {}),
        ...(event.extra ? { extra: redactValue(event.extra) as Record<string, unknown> } : {}),
        ...(event.contexts ? { contexts: redactValue(event.contexts) } : {}),
        ...(event.breadcrumbs ? { breadcrumbs: redactValue(event.breadcrumbs) } : {}),
        ...(event.message ? { message: redactString(event.message) } : {}),
        ...(event.exception
          ? {
              exception: {
                ...event.exception,
                values: event.exception.values?.map((exceptionValue) => ({
                  ...exceptionValue,
                  ...(exceptionValue.value ? { value: redactString(exceptionValue.value) } : {}),
                  ...(exceptionValue.stacktrace
                    ? { stacktrace: redactValue(exceptionValue.stacktrace) }
                    : {}),
                })),
              },
            }
          : {}),
      };
      return safeEvent as ErrorEvent;
    },

    // 发送默认的 PII 数据 (IP 地址等)
    sendDefaultPii: false, // 出于隐私考虑,默认禁用
  });

  // 设置全局标签 (便于在 Sentry Dashboard 中筛选)
  Sentry.setTag("runtime", "cloudflare-workers");
  Sentry.setTag("framework", "tanstack-start");

  isInitialized = true;
}

/**
 * 导出 Sentry 常用函数
 */
export { Sentry };
